import { FilteredCreditApplication } from './types/FilteredCreditApplication'
import { Constants, Enum } from './types/constants'
import { calculateMonthlyAmount } from '../containers/utils/utils'

export const creditApplicationStateSelectors = {
  isReadyForFlinks: (creditApplication: FilteredCreditApplication | null) => {
    if (creditApplication) {
      return (
        !creditApplication.allIncomesConfirmed &&
        creditApplication.status === Enum.ECreditApplicationStatus.Active &&
        creditApplication.requiredExternalSteps.some(
          (x) => x.status === Enum.ERequiredExternalStepStatus.WaitingForClient && x.externalStepId === 'bankAccount',
        ) &&
        creditApplication.finalDecision.decision === Enum.EFinalDecision.Approved
      )
    }
    return false
  },

  isAwaitingPhotoIdDecisionForIdentification: (creditApplication: FilteredCreditApplication | null) => {
    if (creditApplication) {
      return (
        (creditApplication.status === Enum.ECreditApplicationStatus.Draft &&
          !creditApplication.applicantHasCreditReport &&
          creditApplication.requiredDocuments.some(
            (x) =>
              x.typeId === Enum.EDocumentType.ValidPhotoId &&
              x.status === Enum.EDocumentStatus.AwaitingApproval &&
              x.applicantType === Enum.EApplicantType.Applicant,
          )) ||
        (creditApplication.hasCoapplicant &&
          !creditApplication.coApplicantHasCreditReport &&
          creditApplication.finalDecision.decision === Enum.EFinalDecision.Error &&
          creditApplication.requiredDocuments.some(
            (x) =>
              x.typeId === Enum.EDocumentType.ValidPhotoId &&
              x.status === Enum.EDocumentStatus.AwaitingApproval &&
              x.applicantType === Enum.EApplicantType.Coapplicant,
          ))
      )
    }
    return false
  },

  hasRequiredDocumentPendingForUpload: (creditApplication: FilteredCreditApplication | null) => {
    if (creditApplication) {
      return (
        creditApplication.finalDecision.decision !== Enum.EFinalDecision.Refused &&
        creditApplication.requiredDocuments.some(
          (x) =>
            [
              Enum.EDocumentStatus.AwaitingDocument,
              Enum.EDocumentStatus.Refused,
              Enum.EDocumentStatus.Incomplete,
            ].includes(x.status) && x.typeId !== Enum.EDocumentType.SignedCVT,
        )
      )
    }
    return false
  },

  mustShowRequiredPhotoId: (creditApplication: FilteredCreditApplication | null) => {
    if (creditApplication) {
      return (
        creditApplication.status === Enum.ECreditApplicationStatus.Draft &&
        !creditApplication.applicantHasCreditReport &&
        creditApplication.requiredDocuments.some(
          (x) =>
            [
              Enum.EDocumentStatus.AwaitingDocument,
              Enum.EDocumentStatus.Refused,
              Enum.EDocumentStatus.Incomplete,
            ].includes(x.status) && x.typeId === Enum.EDocumentType.ValidPhotoId,
        )
      )
    }
    return false
  },

  canReviewFinalDecision: (creditApplication: FilteredCreditApplication | null) => {
    if (creditApplication) {
      return (
        creditApplication.status !== Enum.ECreditApplicationStatus.Draft &&
        ((creditApplication.finalDecision.decision !== Enum.EFinalDecision.Error &&
          !creditApplication.requiredExternalSteps.every(
            (x) =>
              [Enum.ERequiredExternalStepStatus.Completed, Enum.ERequiredExternalStepStatus.WaitingForBank].includes(
                x.status,
              ) && x.externalStepId === 'bankAccount',
          )) ||
          creditApplication.finalDecision.decision !== Enum.EFinalDecision.Approved)
      )
    }
    return false
  },

  isReadyForWorksheet: (creditApplication: FilteredCreditApplication | null) => {
    if (creditApplication) {
      return (
        creditApplication.requiredDocuments.every((x) => x.status === Enum.EDocumentStatus.Approved) &&
        creditApplication.allIncomesConfirmed &&
        creditApplication.worksheet === null
      )
    }
    return false
  },

  hasPrequalifyDecision: (creditApplication: FilteredCreditApplication | null) => {
    if (creditApplication) {
      return creditApplication.prequalificationDecision !== null
    }
    return false
  },

  hasFinalDecision: (creditApplication: FilteredCreditApplication | null) => {
    if (creditApplication) {
      return (
        creditApplication.prequalificationDecision !== null &&
        creditApplication.finalDecision.decision !== Enum.EFinalDecision.Pending
      )
    }
    return false
  },

  canPostQualify: (creditApplication: FilteredCreditApplication | null) => {
    if (creditApplication) {
      return (
        creditApplication.status === Enum.ECreditApplicationStatus.Draft &&
        creditApplication.prequalificationDecision !== null &&
        creditApplication.prequalificationDecision.decision === Enum.EPrequalificationDecision.Approved
      )
    }
    return false
  },

  failedPrequalify: (creditApplication: FilteredCreditApplication | null) => {
    if (creditApplication) {
      return (
        creditApplication?.prequalificationDecision?.decision === Enum.EPrequalificationDecision.Refused &&
        creditApplication.applicantHasCreditReport
      )
    }
    return false
  },

  failedToConfirmApplicantIdentity: (creditApplication: FilteredCreditApplication | null) => {
    if (creditApplication) {
      return (
        !creditApplication.applicantHasCreditReport &&
        !creditApplication.requiredDocuments.some(
          (x) =>
            [Enum.EDocumentStatus.Approved, Enum.EDocumentStatus.AwaitingApproval].includes(x.status) &&
            x.typeId === Enum.EDocumentType.ValidPhotoId &&
            x.applicantType === Enum.EApplicantType.Applicant,
        )
      )
    }
    return false
  },

  failedToConfirmCoApplicantIdentity: (creditApplication: FilteredCreditApplication | null) => {
    if (creditApplication) {
      return (
        creditApplication.hasCoapplicant &&
        !creditApplication.coApplicantHasCreditReport &&
        !creditApplication.requiredDocuments.some(
          (x) =>
            [Enum.EDocumentStatus.Approved, Enum.EDocumentStatus.AwaitingApproval].includes(x.status) &&
            x.typeId === Enum.EDocumentType.ValidPhotoId &&
            x.applicantType === Enum.EApplicantType.Coapplicant,
        )
      )
    }
    return false
  },

  getMinPaymentForRequestedAmount: (creditApplication: FilteredCreditApplication | null) => {
    if (creditApplication && creditApplication.prequalificationDecision) {
      const amount = Math.min(
        creditApplication?.requestedLoanAmount ?? Number.MAX_VALUE,
        creditApplication?.prequalificationDecision.maxLoanAmount ?? Number.MAX_VALUE,
      )
      return calculateMonthlyAmount(
        creditApplication?.applicantAge >= Constants.UpperThresholdAgeForApplicant
          ? Constants.CorrectedMaxTerm
          : Constants.BaseMaxTerm,
        creditApplication?.prequalificationDecision.minInterestRate,
        amount,
        Constants.FinanceFeeRateForRegular,
      )
    }
    return 0
  },

  getMaxPaymentForRequestedAmount: (creditApplication: FilteredCreditApplication | null) => {
    if (creditApplication && creditApplication.prequalificationDecision) {
      const amount = Math.min(
        creditApplication?.requestedLoanAmount ?? Number.MAX_VALUE,
        creditApplication?.prequalificationDecision.maxLoanAmount ?? Number.MAX_VALUE,
      )
      return calculateMonthlyAmount(
        creditApplication?.applicantAge >= Constants.UpperThresholdAgeForApplicant
          ? Constants.CorrectedMaxTerm
          : Constants.BaseMaxTerm,
        creditApplication?.prequalificationDecision.maxInterestRate > 0
          ? creditApplication?.prequalificationDecision.maxInterestRate
          : 29.99,
        amount,
        Constants.FinanceFeeRateForRegular,
      )
    }
    return 0
  },
}
